import React, { useEffect, useState, useRef } from 'react';
import clipperVacuum from '../../assets/clipper-vacuum.webp';

// מבנה חלקיק - נראה כמו שיער אמיתי
interface Particle {
  id: number;
  x: number;        // מיקום אופקי (%)
  y: number;        // מיקום אנכי (%)
  size: number;     // גודל (רוחב בpx)
  length: number;   // אורך (גובה בpx)
  color: string;    // צבע
  rotation: number; // זווית סיבוב
  speed: number;    // מהירות נפילה
  drift: number;    // תנועה אופקית
  opacity: number;  // שקיפות
}

// שלבי האנימציה
type Stage = 'floating' | 'sucking' | 'empty' | 'new-message';

interface VacuumEffectProps {
  isInView?: boolean; // האם הסקשן נראה על המסך
}

const VacuumEffect: React.FC<VacuumEffectProps> = ({ isInView = true }) => {
  const [particles, setParticles] = useState<Particle[]>([]);
  const [stage, setStage] = useState<Stage>('floating');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const nextParticleIdRef = useRef(0); // מעקב אחרי ID הבא
  const vacuumStartTimeRef = useRef<number | null>(null); // זמן התחלת השאיבה - לסנכרון!
  const savedMachinePositionRef = useRef<{ x: number; y: number } | null>(null); // מיקום קבוע של המכונה!
  
  // צבעי החלקיקים
  const colors = [
    '#FFFFFF', // לבן (40%)
    '#FFE5B4', // זהב בהיר מאוד (30%)
    '#F5B658', // זהב בסיס (20%)
    '#D4943A', // זהב כהה (10%)
  ];
  
  // פונקציה ליצירת חלקיקים
  const createParticles = React.useCallback((count: number): Particle[] => {
    const newParticles: Particle[] = [];
    
    // התאמה למובייל - פחות חלקיקים במסכים קטנים
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const adjustedCount = isMobile ? Math.floor(count / 2) : count; // מובייל: חצי מהכמות
    
    for (let i = 0; i < adjustedCount; i++) {
      const colorRandom = Math.random();
      let color;
      if (colorRandom < 0.4) color = colors[0];
      else if (colorRandom < 0.7) color = colors[1];
      else if (colorRandom < 0.9) color = colors[2];
      else color = colors[3];
      
      // גודל מותאם - במובייל קצת יותר גדול
      const sizeMultiplier = isMobile ? 1.25 : 1;
      
      newParticles.push({
        id: nextParticleIdRef.current++,
        x: 5 + Math.random() * 90,
        y: 5 + Math.random() * 95,
        size: (2.5 + Math.random() * 2) * sizeMultiplier,
        length: (2.5 + Math.random() * 2) * sizeMultiplier,
        color: color,
        rotation: -20 + Math.random() * 40,
        speed: 0.1 + Math.random() * 0.2,
        drift: -0.08 + Math.random() * 0.16,
        opacity: 0.5 + Math.random() * 0.4,
      });
    }
    
    return newParticles;
  }, [colors]);
  
  // יצירת כמות קבועה של חלקיקים מהתחלה - 100 חלקיקים!
  useEffect(() => {
    setParticles(createParticles(100));
  }, []); // רק פעם אחת בהתחלה!
  
  // האזנה לאות מ-HeroSection כשהמילה השלישית נגמרת
  useEffect(() => {
    const handleVacuumStart = () => {
      startVacuumSequence();
    };
    
    window.addEventListener('startVacuum', handleVacuumStart);
    
    return () => {
      window.removeEventListener('startVacuum', handleVacuumStart);
    };
  }, []);
  
  // פונקציית השאיבה - מופעלת כשמתקבל האות
  const startVacuumSequence = () => {
    setStage('sucking');
    vacuumStartTimeRef.current = performance.now(); // שמירת זמן התחלה - לסנכרון!
    
    // ✅ תיקון באג: חישוב מיקום המכונה מחדש בכל לופ + תמיכה בגלילה
    // הסרנו את ה-if כדי לחשב מחדש בכל פעם (תיקון לופים שונים)
    // הוספנו window.scrollX/Y כדי לתמוך בגלילה (תיקון חצי מסך)
    const machineRect = document.querySelector('.vacuum-machine')?.getBoundingClientRect();
    if (machineRect) {
      savedMachinePositionRef.current = {
        x: machineRect.left + window.scrollX + machineRect.width * 0.65,
        y: machineRect.top + window.scrollY + machineRect.height * 0.30 + 20
      };
    }
    
    // אחרי 3.5 שניות - מחק את החלקיקים (אחרי המערבולת והשאיבה!)
    setTimeout(() => {
      setParticles([]);
      vacuumStartTimeRef.current = null; // איפוס!
      // לא מאפסים את savedMachinePositionRef - נשאר קבוע!
    }, 3500); // 3.5 שניות - יותר זמן לראות את האפקטים!
    
    const container = document.querySelector('.container.mx-auto');
    if (container instanceof HTMLElement) {
      container.style.zIndex = '5';
    }
    
    const heroText = document.querySelector('.first-sentence-slide-in');
    const heroH2 = document.querySelector('.hero-subtitle');
    
    if (heroText instanceof HTMLElement) {
      // לא מפרקים לאותיות! המשפט כולו זז
      const rect = heroText.getBoundingClientRect();
      
      // שאיבה של 2 שניות - מהיר ודינמי!
      const startTime = performance.now();
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        
        // אנימציה חלקה אחת - ללא קפיצות! 🚀
        // ✅ שונה מ-1800 ל-800 - שאיבה מהירה יותר (0.8 שניות במקום 1.8)
        const progress = Math.min(elapsed / 800, 1); // 0.8 שניות - מהיר ודינמי!
        
        // שימוש במיקום השמור של המכונה - קבוע בכל הלופים!
        const targetX = savedMachinePositionRef.current?.x || 0;
        const targetY = savedMachinePositionRef.current?.y || 0;
        if (!targetX || !targetY) return;
        
        // ✅ תיקון באג: הוספת window.scrollX/Y למיקום הטקסט
        // כדי שהטקסט והמכונה יהיו באותה מערכת קואורדינטות
        const startX = rect.left + window.scrollX + rect.width / 2;
        const startY = rect.top + window.scrollY + rect.height / 2;
        
        // מיקום נוכחי
        let x = startX + (targetX - startX) * progress;
        let y = startY + (targetY - startY) * progress;
        
        // תנועה למטה בסוף - נכנס למשובץ!
        if (progress > 0.8) {
          const downProgress = (progress - 0.8) / 0.2;
          y += downProgress * 25; // תנועה 25px למטה - לכיוון המשובץ!
        }
        
        // easing חלק - cubic-bezier(0.4, 0.0, 0.2, 1) מקצועי!
        const t = progress;
        const easedProgress = t < 0.5 
          ? 4 * t * t * t  // התחלה איטית
          : 1 - Math.pow(-2 * t + 2, 3) / 2; // סוף מהיר
        
        const blur = easedProgress * 15; // טשטוש חלק
        const scale = 1 - easedProgress * 0.85; // התקטנות ל-15%
        
        // עיוות מקצועי - המילים נמתחות ונעוותות בזמן השאיבה
        const skewX = easedProgress * -25; // עיוות אופקי עד -25 מעלות
        const scaleX = 1 + easedProgress * 0.8; // מתיחה אופקית עד 180%
        
        // opacity מבוסס על מיקום אמיתי - נעלם רק כשמגיע למכונה!
        const machineTopY = targetY; // משתמשים במיקום השמור!
        const distanceToMachine = Math.abs(y - machineTopY);
        const opacity = distanceToMachine > 60 
          ? 1  // רחוק מהמכונה - מלא
          : Math.max(0, distanceToMachine / 60); // קרוב - נעלם בהדרגה
        
        heroText.style.transform = `translate(${x - startX}px, ${y - startY}px) scale(${scale}) scaleX(${scaleX}) skewX(${skewX}deg)`;
        heroText.style.opacity = String(opacity);
        heroText.style.filter = `blur(${blur}px) brightness(${1 + progress * 0.5})`;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
        // בסוף האנימציה, ה-opacity כבר 0 מהחישוב למעלה!
      };
      
      requestAnimationFrame(animate);
      
      // 🔧 FIX: הסתר את ה-h2 מיד אחרי השאיבה - לא לחכות 2 שניות!
      // הטקסט כבר נעלם (opacity: 0) אחרי 800ms, אז אפשר להסתיר אותו מהר יותר
      setTimeout(() => {
        if (heroH2 instanceof HTMLElement) {
          heroH2.style.display = 'none';
        }
      }, 900); // 🚀 שונה מ-2000 ל-900 - הטקסט נעלם מיד אחרי השאיבה!
    }
    
    // 3.5 שניות - מסך ריק (אחרי שהשאיבה נגמרת)
    setTimeout(() => {
      setStage('empty');
    }, 3500);
    
    // 1.9 שניות - הצג משפט חדש (שונה מ-4000 ל-1900 - קיצור זמן המתנה)
    setTimeout(() => {
      setStage('new-message');
      setShowNewMessage(true);
    }, 1900);
    
    // 4.4 שניות - הסתר משפט והחזר טקסט (שונה מ-6500 ל-4400 - 2.5 שניות אחרי שהמשפט מופיע)
    setTimeout(() => {
      setShowNewMessage(false);
      
      const heroText = document.querySelector('.first-sentence-slide-in');
      const heroH2 = document.querySelector('.hero-subtitle');
      
      const container = document.querySelector('.container.mx-auto');
      if (container instanceof HTMLElement) {
        container.style.zIndex = '10';
      }
      
      if (heroH2 instanceof HTMLElement) {
        heroH2.style.display = 'block';
        heroH2.style.visibility = 'visible';
        heroH2.style.opacity = '1';
      }
      
      if (heroText instanceof HTMLElement) {
        heroText.classList.remove('vacuum-suck-active', 'text-vacuum-suck');
        heroText.style.display = 'flex';
        heroText.style.visibility = 'visible';
        heroText.style.opacity = '0';
        heroText.style.transform = 'translateX(0)';
        heroText.style.filter = 'none';
        heroText.style.animation = 'none';
        
        setTimeout(() => {
          if (heroText instanceof HTMLElement) {
            heroText.style.transition = 'opacity 1s ease-in';
            heroText.style.opacity = '1';
          }
        }, 50);
      }
      
      setStage('floating');
      
      // התחלת הלופ הבא עם חלקיקים חדשים - 100 חלקיקים!
      setTimeout(() => {
        setParticles(createParticles(100));
      }, 500); // חצי שנייה אחרי שהמשפט נעלם
      
    }, 4400); // 4.4 שניות - משפט מופיע 2.5 שניות! (שונה מ-6500)
  };
  
  // אנימציה חלקה - החלקיקים צפים, רועדים או נשאבים
  useEffect(() => {
    // עצירה רק כשיוצאים מהסקשן וזה לא שאיבה!
    // אם זה שאיבה - תן לה להסתיים!
    if (!isInView && stage !== 'sucking') return;
    
    // לא מצייר חלקיקים בשלב 'new-message' - רק מסך ריק!
    // גם לא מצייר בשלב 'sucking' אחרי 3.5 שניות (כשהם נמחקים)
    if (particles.length === 0 || stage === 'empty' || stage === 'new-message') return;
    
    // ביטול אנימציה קודמת אם יש
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    let lastTime = performance.now();
    let lastUpdateTime = 0;
    // ⚡ Performance Optimization: שונה מ-150ms ל-250ms (מ-6.67 FPS ל-4 FPS)
    // סיבה: 40% פחות עדכונים = פחות עומס על הדפדפן, אותה חוויה ויזואלית
    const throttleDelay = 250; // 4 FPS - חיסכון 60%
    
    const animate = (currentTime: number) => {
      // Throttle - רק כל 100ms (10 פעמים בשנייה)
      if (currentTime - lastUpdateTime < throttleDelay) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateTime = currentTime;
      
      const deltaTime = (currentTime - lastTime) / 16.67;
      lastTime = currentTime;
      
      setParticles(prevParticles =>
        prevParticles.map(particle => {
          if (stage === 'sucking') {
            // 🌪️ מערבולת מקצועית עם 3 שלבים!
            
            // נקודת היעד - החלק המשובץ של המכונה (מרכז השאיבה)
            const targetX = 22; // ימינה עוד 2 סמ - מדויק על המשובץ!
            const targetY = 67; // למעלה 2 סמ - מדויק על המשובץ!
            
            const dx = targetX - particle.x;
            const dy = targetY - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // זמן שעבר מתחילת השאיבה
            const elapsed = vacuumStartTimeRef.current 
              ? currentTime - vacuumStartTimeRef.current 
              : 0;
            
            let newX = particle.x;
            let newY = particle.y;
            let newSize = particle.size;
            let newLength = particle.length;
            let newOpacity = particle.opacity;
            
            // 🎯 שלב 1 (0-1000ms): תנועה מהירה לכיוון המשובץ
            if (elapsed < 1000) {
              const speed = 2.5;
              newX = particle.x + dx * speed * deltaTime / 60;
              newY = particle.y + dy * speed * deltaTime / 60;
            }
            // 🌀 שלב 2 (1000-2000ms): מערבולת ספירלית סביב המשובץ!
            else if (elapsed < 2000) {
              const vortexProgress = (elapsed - 1000) / 1000; // 0 → 1
              const angle = particle.id * 0.5 + currentTime * 0.005; // זווית ייחודית לכל חלקיק
              const radius = distance * (1 - vortexProgress * 0.35); // רדיוס מתכווץ פחות - חלקיקים נפרדים!
              
              // תנועה ספירלית לכיוון המרכז!
              const orbitX = targetX + Math.cos(angle) * radius;
              const orbitY = targetY + Math.sin(angle) * radius;
              
              const orbitDx = orbitX - particle.x;
              const orbitDy = orbitY - particle.y;
              
              const orbitSpeed = 4.0; // מהיר יותר - מערבולת חזקה!
              newX = particle.x + orbitDx * orbitSpeed * deltaTime / 60;
              newY = particle.y + orbitDy * orbitSpeed * deltaTime / 60;
              
              // התכווצות קלה במערבולת - פחות מאשר קודם
              const shrink = 1 - vortexProgress * 0.15;
              newSize = particle.size * shrink;
              newLength = particle.length * shrink;
            }
            // 💨 שלב 3 (2000-3000ms): שאיבה פנימה ביחד - כולם נעלמים!
            else {
              const finalProgress = Math.min((elapsed - 2000) / 1000, 1); // 0 → 1
              
              // כולם נשאבים פנימה במהירות גבוהה מאוד!
              const finalSpeed = 8.0; // מהיר פי 2!
              newX = particle.x + dx * finalSpeed * deltaTime / 60;
              newY = particle.y + dy * finalSpeed * deltaTime / 60;
              
              // התכווצות והעלמה מהירה!
              const shrink = 1 - finalProgress * 0.8;
              newSize = particle.size * shrink;
              newLength = particle.length * shrink;
              newOpacity = particle.opacity * (1 - finalProgress);
            }
            
            return {
              ...particle,
              x: newX,
              y: newY,
              size: newSize,
              length: newLength,
              opacity: newOpacity,
              rotation: particle.rotation + 20 * deltaTime, // סיבוב מהיר!
            };
          }
          
          let newY = particle.y + particle.speed * deltaTime;
          let newX = particle.x + particle.drift * deltaTime;
          let newRotation = particle.rotation + 0.5 * deltaTime;
          
          if (newY > 95) {
            newY = 5;
            newX = 40 + Math.random() * 55;
          }
          
          return {
            ...particle,
            y: newY,
            x: newX,
            rotation: newRotation,
          };
        })
      );
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [particles.length, stage, isInView]); // רק length, לא כל particles!
  
  return (
    <div 
      className="vacuum-effect-container"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 15, // גבוה מאוד - מעל המכונה!
      }}
    >
      {/* החלקיקים - נראים כמו שיער - לא מציירים בזמן המשפט */}
      {stage !== 'new-message' && particles.map(particle => (
        <div
          key={particle.id}
          className={stage === 'sucking' ? 'particle-sucking' : 'particle-floating'}
          style={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.length}px`,
            background: `linear-gradient(180deg, ${particle.color} 0%, transparent 100%)`,
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            transform: `rotate(${particle.rotation}deg)`,
            opacity: particle.opacity,
            transition: 'none',
            pointerEvents: 'none',
            zIndex: 10,
            willChange: 'transform, opacity',
          }}
        />
      ))}
      
      {/* המכונה - למטה-שמאל, אלכסונית - עם אור כתום בהיר */}
      <div
        className={`vacuum-machine ${stage === 'sucking' ? 'is-sucking' : ''}`}
        style={{
          position: 'absolute',
          left: 'clamp(-10px, 1vw, 30px)', // מובייל: -10px, דסקטופ: 30px
          top: 'clamp(85%, 80%, 80%)', // מובייל: 85%, דסקטופ: 80%
          transform: 'translateY(-50%) rotate(45deg)',
          width: 'clamp(180px, 30vw, 450px)', // מובייל: 180px, דסקטופ: 450px
          height: 'auto',
          zIndex: stage === 'sucking' ? 1 : 15, // דינמי! בשאיבה - מתחת, בזמן רגיל - מעל!
        }}
      >
        <div
          className="vacuum-glow"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '150%',
            height: '150%',
            borderRadius: '50%',
            zIndex: -1,
            background: 'radial-gradient(circle, rgba(255, 165, 0, 0.15) 0%, transparent 70%)', // ✏️ שונה מ-0.3 ל-0.15 (50% פחות עוצמה - אור עדין יותר)
            filter: 'blur(20px)',
          }}
        />
        <div
          className="vacuum-glow-burst"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '170%',
            height: '170%',
            borderRadius: '50%',
            zIndex: -2,
            background: 'radial-gradient(circle, rgba(255, 180, 80, 0.25) 0%, transparent 65%)', // ✏️ שונה מ-0.55 ל-0.25 (55% פחות עוצמה - אור עדין יותר)
            filter: 'blur(28px)',
          }}
        />
        <div className="vacuum-top-waves">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
        
        <img 
          src={clipperVacuum} 
          alt="מכונת תספורת"
          width="672"
          height="1027"
          className="vacuum-machine-body"
          style={{
            width: '100%',
            aspectRatio: '672/1027', // 🔧 CLS FIX: שומר יחס גובה-רוחב מראש - חוסך חישובי Layout
            filter: 'drop-shadow(0 6px 16px rgba(0, 0, 0, 0.4))',
          }}
          loading="eager"
        />

      </div>
      
      {/* המשפט החדש - "מישהו הסתפר פה?!" - אותיות נופלות */}
      {showNewMessage && (
        <div
          style={{
            position: 'absolute',
            top: '35%',
            left: 'clamp(50%, 55%, 60%)', // מובייל: 50% (מרכז), דסקטופ: 60%
            transform: 'translate(-50%, -50%)',
            fontSize: 'clamp(2rem, 6.5vw, 4.8rem)', // מובייל: 32px, דסקטופ: 77px
            fontWeight: 900,
            textAlign: 'center',
            direction: 'rtl',
            zIndex: 15,
            display: 'flex',
            gap: 'clamp(0.2rem, 0.5vw, 0.4rem)',
            minHeight: 'clamp(60px, 10vw, 100px)', // 🔧 CLS FIX: שומר מקום מראש למשפט - מונע Layout Shift
          }}
        >
          {'מישהו הסתפר פה?!'.split('').map((letter, index) => (
            <span
              key={index}
              style={{
                display: 'inline-block',
                color: '#FFFFFF',
                // animation: `letterDrop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.05}s both`,
               animation: `letterDrop 0.6s ease-out ${index * 0.05}s both`,//שינוי של אנמציה שיופל מלעמה בלי קפיצות
                textShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </span>
          ))}
        </div>
      )}
      
    </div>
  );
};

export default VacuumEffect;